/**
 * WindowsDriverHelp — walkthrough shown when a printer is selected but
 * its interface can't be claimed over WebUSB. On Windows this is almost
 * always because the OS bound its built-in print driver to the device;
 * replacing it with WinUSB (via Zadig) frees it for WebUSB.
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, AlertTriangle } from "lucide-react";

const STEPS = [
  "Download Zadig (free, tiny) from zadig.akeo.ie and open it — no install needed.",
  "Make sure the Tecor TR-422B is plugged in and powered on.",
  "In Zadig, open the Options menu and tick \u201cList All Devices\u201d.",
  "In the dropdown, select your Tecor printer (it may show as \u201cUSB Printing Support\u201d or the model name).",
  "Set the target driver on the right to \u201cWinUSB\u201d using the arrows.",
  "Click \u201cReplace Driver\u201d (or \u201cInstall Driver\u201d) and wait for it to finish.",
  "Come back here, click \u201cConnect printer\u201d and pick the Tecor \u2014 it will now claim successfully.",
];

export default function WindowsDriverHelp({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="driver-help-dialog">
        <DialogHeader>
          <DialogTitle>Printer found, but Windows is holding it</DialogTitle>
          <DialogDescription>
            Your browser could see the printer but couldn&apos;t take control of it.
            On Windows, swap its driver to WinUSB so the app can print directly.
          </DialogDescription>
        </DialogHeader>

        <ol className="space-y-3 text-sm list-decimal pl-5" data-testid="driver-help-steps">
          {STEPS.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>

        <div className="flex items-start gap-2 rounded-lg bg-amber-50 text-amber-900 p-3 text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            This changes the driver for this printer to WinUSB. If you ever want to use it
            as a normal Windows printer again, revert it in Device Manager
            (right-click the device &rarr; Uninstall device &rarr; unplug &amp; replug).
          </span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <a href="https://zadig.akeo.ie" target="_blank" rel="noreferrer" data-testid="driver-help-zadig-link">
            <Button className="bg-ochre-600 hover:bg-ochre-400 text-white gap-2">
              <ExternalLink className="w-4 h-4" /> Open Zadig
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
